<template>
  <v-container fluid class="container">
    <v-card class="mb-4">
      <v-card-title class="pb-2">User Activity Logs</v-card-title>
      <v-card-text>
        <v-row dense align="end">
          <v-col cols="12" sm="6" md="2">
            <v-text-field
              v-model="filters.username"
              label="Username"
              clearable
              dense
              hide-details
              outlined
            />
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="filters.activity"
              label="Activity"
              clearable
              dense
              hide-details
              outlined
            />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-text-field
              v-model="filters.model"
              label="Planning ID"
              clearable
              dense
              hide-details
              outlined
            />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-text-field
              v-model="filters.from"
              type="date"
              label="From"
              clearable
              dense
              hide-details
              outlined
            />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-text-field
              v-model="filters.to"
              type="date"
              label="To"
              clearable
              dense
              hide-details
              outlined
            />
          </v-col>
        </v-row>
        <v-row dense align="end">
          <v-col cols="12" sm="6" md="4" class="d-flex align-end">
            <v-btn color="primary" class="mr-2" :loading="loading" @click="applyFilters">
              Apply filters
            </v-btn>
            <v-btn text @click="resetFilters">
              Reset
            </v-btn>
          </v-col>
          <v-col cols="12" sm="6" md="4" class="d-flex justify-end">
            <vue-excel-xlsx
              :data="excelData"
              :columns="excelColumns"
              :name="excelFileName"
              :sheetname="'User Activity Logs'"
            >
              <v-btn color="success" :disabled="!activities.length">
                Export XLSX
              </v-btn>
            </vue-excel-xlsx>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-alert v-if="errorText" type="error" dense outlined class="mb-4">
      {{ errorText }}
    </v-alert>

    <v-data-table
      :headers="headers"
      :items="activities"
      :server-items-length="totalItems"
      :loading="loading"
      :options.sync="options"
      :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
      @update:options="fetchActivityLogs"
      class="elevation-1"
      item-key="id"
      dense
    >
      <template v-slot:item.timestamp="{ item }">
        {{ formatTimestamp(item.timestamp) }}
      </template>
      <template v-slot:item.actions="{ item }">
        <template v-if="isLockRow(item)">
          <v-btn
            icon small :loading="downloading === `${item.id}-pdf`"
            :disabled="downloading !== null"
            title="Presentation PDF frozen when this planning was locked"
            @click="downloadLockFile(item, 'pdf')"
          >
            <v-icon small>mdi-file-pdf-box</v-icon>
          </v-btn>
          <v-btn
            icon small :loading="downloading === `${item.id}-excel`"
            :disabled="downloading !== null"
            title="Full Excel report frozen when this planning was locked"
            @click="downloadLockFile(item, 'excel')"
          >
            <v-icon small>mdi-file-excel-box</v-icon>
          </v-btn>
          <v-btn
            icon small :loading="downloading === `${item.id}-snapshot`"
            :disabled="downloading !== null"
            title="Excel listing of what the lock stored in the database"
            @click="downloadLockFile(item, 'snapshot')"
          >
            <v-icon small>mdi-file-excel</v-icon>
          </v-btn>
        </template>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import api from '../api/lbt-api';

const { getUserActivityLogs } = api;
const allowedPerPageOptions = [5, 10, 25, 50, 100];
const timezonePattern = /(?:Z|[+-]\d{2}:?\d{2})$/;
const zagrebTimestampFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Zagreb',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

function formatZagrebTimestamp(date) {
  const parts = {};
  zagrebTimestampFormatter.formatToParts(date).forEach(({ type, value }) => {
    parts[type] = value;
  });
  return `${parts.day}.${parts.month}.${parts.year} ${parts.hour}:${parts.minute}:${parts.second}`;
}

const defaultFilters = () => ({
  username: '',
  activity: '',
  model: '',
  from: '',
  to: '',
});

export default {
  name: 'UserActivityLogs',
  data() {
    return {
      loading: false,
      errorText: '',
      activities: [],
      totalItems: 0,
      options: {
        page: 1,
        itemsPerPage: 10,
      },
      itemsPerPageOptions: allowedPerPageOptions,
      filters: defaultFilters(),
      headers: [
        { text: 'ID', value: 'id' },
        { text: 'Username', value: 'username' },
        { text: 'Activity', value: 'activity' },
        { text: 'Planning ID', value: 'model' },
        { text: 'Timestamp', value: 'timestamp' },
        {
          text: '', value: 'actions', sortable: false, width: 90,
        },
      ],
      downloading: null,
      excelColumns: [
        { label: 'ID', field: 'id' },
        { label: 'Username', field: 'username' },
        { label: 'Activity', field: 'activity' },
        { label: 'Planning ID', field: 'model' },
        { label: 'Timestamp', field: 'timestamp' },
      ],
    };
  },
  computed: {
    excelData() {
      return this.activities.map(item => ({
        ...item,
        timestamp: this.formatTimestamp(item.timestamp),
      }));
    },
    excelFileName() {
      const dateText = new Date().toISOString().slice(0, 10);
      return `user_activity_logs_${dateText}`;
    },
  },
  methods: {
    toPositiveInt(value, fallback) {
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) {
        return fallback;
      }
      const normalized = Math.trunc(parsed);
      return normalized > 0 ? normalized : fallback;
    },
    toPerPage(value, fallback) {
      const parsed = this.toPositiveInt(value, fallback);
      if (!allowedPerPageOptions.includes(parsed)) {
        return fallback;
      }
      return parsed;
    },
    readRouteQuery() {
      const query = this.$route.query || {};
      this.options.page = this.toPositiveInt(query.page, 1);
      this.options.itemsPerPage = this.toPerPage(query.per_page, this.options.itemsPerPage || 10);
      this.filters.username = query.username || '';
      this.filters.activity = query.activity || '';
      this.filters.model = query.model || '';
      this.filters.from = query.from || '';
      this.filters.to = query.to || '';
    },
    updateRouteQuery() {
      const query = {
        page: String(this.options.page || 1),
        per_page: String(this.options.itemsPerPage || 10),
      };
      if (this.filters.username) query.username = this.filters.username;
      if (this.filters.activity) query.activity = this.filters.activity;
      if (this.filters.model) query.model = this.filters.model;
      if (this.filters.from) query.from = this.filters.from;
      if (this.filters.to) query.to = this.filters.to;

      const currentQuery = this.$route.query || {};
      const allKeys = Array.from(new Set([...Object.keys(query), ...Object.keys(currentQuery)]));
      const hasDiff = allKeys.some(key => String(query[key] || '') !== String(currentQuery[key] || ''));
      if (hasDiff) {
        this.$router.replace({ path: this.$route.path, query }).catch(() => {});
      }
    },
    buildQueryParams() {
      return {
        page: this.options.page || 1,
        per_page: this.options.itemsPerPage || 10,
        username: this.filters.username,
        activity: this.filters.activity,
        model: this.filters.model,
        from: this.filters.from,
        to: this.filters.to,
      };
    },
    formatTimestamp(value) {
      if (!value) {
        return '';
      }

      const rawValue = String(value);
      const normalizedValue = timezonePattern.test(rawValue) ? rawValue : `${rawValue}Z`;
      const date = new Date(normalizedValue);
      if (Number.isNaN(date.getTime())) {
        return rawValue.replace('T', ' ').replace('Z', '');
      }

      return formatZagrebTimestamp(date);
    },
    async fetchActivityLogs() {
      this.loading = true;
      this.errorText = '';
      try {
        this.updateRouteQuery();
        const response = await getUserActivityLogs(this.buildQueryParams());
        this.activities = response.items || [];
        this.totalItems = (response.meta && response.meta.total) || 0;
      } catch (e) {
        this.errorText = e.message || 'Failed to fetch activity logs';
        this.activities = [];
        this.totalItems = 0;
      } finally {
        this.loading = false;
      }
    },
    async applyFilters() {
      this.options.page = 1;
      await this.fetchActivityLogs();
    },
    async resetFilters() {
      this.filters = defaultFilters();
      this.options.page = 1;
      await this.fetchActivityLogs();
    },
    isLockRow(item) {
      return typeof item.activity === 'string' && item.activity.startsWith('LOCK planning');
    },
    async downloadLockFile(item, kind) {
      this.downloading = `${item.id}-${kind}`;
      this.errorText = '';
      try {
        const {
          blob, fileName, matchesLock, excelFrozen,
        } = await api.downloadLockedPlanningFile(item.model, kind);
        if (kind === 'pdf' && matchesLock === false) {
          // The cache was rebuilt after the lock, so this is not the document
          // that was produced at lock time. Say so rather than imply otherwise.
          this.errorText = 'This PDF was rebuilt after the planning was locked '
            + 'and no longer matches the one produced at lock time.';
        }
        if (kind === 'excel' && excelFrozen === false) {
          this.errorText = 'No Excel report was frozen when this planning was locked, '
            + 'so the current state of the report was downloaded instead.';
        }
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.errorText = e.message || 'Failed to download file';
      } finally {
        this.downloading = null;
      }
    },
  },
  created() {
    this.readRouteQuery();
    this.fetchActivityLogs();
  },
};
</script>
